/**
 * Crime Ops – Tab "IMMOBILIEN" (Versteck-Upgrade-Verwaltung)
 *
 * Zeigt alle eigenen Verstecke mit Ausbau-Verwaltung, Slot-Equipment,
 * und Inventar. Lädt eigene Daten via /api/v1/hideouts.
 */

import { api, ApiError } from '../../services/api.js';
import { formatItemKey } from '../../utils/formatters.js';
import { formatHideoutName, HIDEOUT_TYPE_LABELS } from '../../utils/hideout-format.js';
import { renderWhenIdle, cancelPendingRender } from '../../utils/background-render.js';
import { calcNoticeDisplayMs } from '../../utils/notice-duration.js';

const IMMO_RENDER_KEY = 'immobilien';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface PropertySlot {
  id:             string;
  slotType:       'GROW_TENT' | 'LABOR_WORKBENCH' | 'LABOR_OUTPUT';
  slotIndex:      number;
  isUnlocked:     boolean;
  tentItem:       string | null;
  lampItem:       string | null;
  potItem:        string | null;
  filterItem:     string | null;
  lampItem2:      string | null;
  potItem2:       string | null;
  lampItem3:      string | null;
  potItem3:       string | null;
  lampItem4:      string | null;
  potItem4:       string | null;
  irrigationItem: string | null;
  stoveItem:      string | null;
  glassItem:      string | null;
  safetyItem:     string | null;
  distillItem:    string | null;
  chemiItem:      string | null;
  capsuleItem:    string | null;
  endgameItem:    string | null;
}

interface InventoryItem {
  id:        string;
  itemType:  string;
  itemKey:   string;
  quantity:  number;
  expiresAt: string | null;
}

// NT-HIDEOUT-PROFILE-01 — Profil-Achsen LAGER/TARNUNG/GARAGE
type AxisKey = 'STORAGE' | 'CAMOUFLAGE' | 'GARAGE';

interface HideoutAxisStatus {
  level:          number;
  base:           number;
  max:            number;
  effectiveValue: number;
  isUpgrading:    boolean;
  completesAt:    string | null;
  /** null = bereits auf Max-Stufe, kein weiterer Ausbau möglich. */
  nextCostCents:  string | null;
  nextMinutes:    number | null;
}

interface HideoutGarageSlot {
  slotIndex:  number;
  vehicleId:  string | null;
  vehicleKey: string | null;
}

interface Hideout {
  id:                          string;
  subType:                     string;
  customName:                  string | null;
  nickname:                    string | null;
  slots:                       number;
  currentLoad:                 number;
  rentPerWeekCents:            string;
  raidRiskPerFL:               number;
  expansionType:               'GROW_ROOM' | 'LABOR' | null;
  expansionLevel:              number;
  expansionUpgradeStartedAt:   string | null;
  expansionUpgradeCompletesAt: string | null;
  isMobile:                    boolean;
  suspicionLevel:              number;
  isActive:                    boolean;
  propertySlots:               PropertySlot[];
  inventory:                   InventoryItem[];
  axisStatus:                  { storage: HideoutAxisStatus; camouflage: HideoutAxisStatus; garage: HideoutAxisStatus };
  garageSlots:                 HideoutGarageSlot[];
}

interface VehicleListItem {
  id:         string;
  vehicleKey: string;
  label:      string;
  role:       string;
  /** NT-VIERTEL-FIX-01 (V1): Stellplatz-Pflicht / aktueller Heimatplatz / umstellbar. */
  requiresStellplatz:   boolean;
  stellplatzPropertyId: string | null;
  canMoveStellplatz:    boolean;
}


const ITEM_TYPE_LABELS: Record<string, string> = {
  MATERIAL:       'Material',
  LABOR_INPUT:    'Labor-Zutat',
  LABOR_OUTPUT:   'Labor-Produkt',
  LAB_EQUIPMENT:  'Labor-Equipment',
  FENCE_GOODS:    'Hehlerware',
  GARDEN:         'Garten',
  GARDEN_OUTPUT:  'Ernte',
  TOBACCO_WET:    'Tabak (frisch)',
  TOBACCO_DRIED:  'Tabak (getrocknet)',
  TOBACCO_CUT:    'Tabak (Feinschnitt)',
  ROLLING_SUPPLY: 'Rolltisch-Zubehör',
  ROLLING_OUTPUT: 'Fertigprodukt',
  VEHICLE:        'Fahrzeug',
};

// ─── Lokale Konfiguration (spiegelt Backend EXPANSION_COSTS / LABELS) ─────────

const MAX_GROW:  Record<string, number> = {
  dachboden: 2, hinterzimmer: 2, wohnung: 3, kellerstudio: 2, gartenlaube: 3,
  campingwagen: 2, bungalow: 3, luxus_apartment: 3, lagercontainer: 2, bootshaus: 3, bunker: 3,
  // NT-HIDEOUT-PROFILE-01 — neu (Backend: hideout.definitions.ts::MAX_GROW_LEVELS)
  waschkeller: 1, kirchturm: 2, tiefgaragenabteil: 0, schrebergarten: 3,
  doenerbude: 0, kegelbahn: 3, tankstelle: 0, sparkassenfiliale: 0,
};
const MAX_LABOR: Record<string, number> = {
  dachboden: 1, hinterzimmer: 2, wohnung: 2, kellerstudio: 3, gartenlaube: 1,
  campingwagen: 2, bungalow: 3, luxus_apartment: 3, lagercontainer: 3, bootshaus: 2, bunker: 3,
  // NT-HIDEOUT-PROFILE-01 — neu (Backend: hideout.definitions.ts::MAX_LABOR_LEVELS)
  waschkeller: 2, kirchturm: 0, tiefgaragenabteil: 0, schrebergarten: 0,
  doenerbude: 3, kegelbahn: 1, tankstelle: 2, sparkassenfiliale: 0,
};

// Ausbaukosten in Cent für Bestätigungs-Dialog (entspricht Backend EXPANSION_COSTS)
const EXPAND_CENTS: Record<string, Record<string, Record<number, number>>> = {
  dachboden:      { GROW_ROOM: { 1: 30_000, 2: 50_000 },                         LABOR: { 1: 40_000 } },
  hinterzimmer:   { GROW_ROOM: { 1: 40_000, 2: 70_000 },                         LABOR: { 1: 60_000, 2: 120_000 } },
  wohnung:        { GROW_ROOM: { 1: 60_000, 2: 100_000, 3: 180_000 },            LABOR: { 1: 80_000, 2: 180_000 } },
  kellerstudio:   { GROW_ROOM: { 1: 70_000, 2: 120_000 },                         LABOR: { 1: 90_000, 2: 180_000, 3: 400_000 } },
  gartenlaube:    { GROW_ROOM: { 1: 40_000, 2: 60_000, 3: 90_000 },             LABOR: { 1: 50_000 } },
  campingwagen:   { GROW_ROOM: { 1: 70_000, 2: 120_000 },                         LABOR: { 1: 80_000, 2: 150_000 } },
  bungalow:       { GROW_ROOM: { 1: 120_000, 2: 200_000, 3: 350_000 },          LABOR: { 1: 150_000, 2: 280_000, 3: 550_000 } },
  luxus_apartment:{ GROW_ROOM: { 1: 200_000, 2: 350_000, 3: 600_000 },          LABOR: { 1: 250_000, 2: 450_000, 3: 800_000 } },
  lagercontainer: { GROW_ROOM: { 1: 90_000, 2: 160_000 },                         LABOR: { 1: 100_000, 2: 200_000, 3: 450_000 } },
  bootshaus:      { GROW_ROOM: { 1: 140_000, 2: 220_000, 3: 380_000 },          LABOR: { 1: 160_000, 2: 300_000 } },
  bunker:         { GROW_ROOM: { 1: 500_000, 2: 900_000, 3: 1_500_000 },        LABOR: { 1: 600_000, 2: 1_100_000, 3: 2_000_000 } },
  // NT-HIDEOUT-PROFILE-01 — neu
  waschkeller:       { GROW_ROOM: { 1: 45_000 },                                LABOR: { 1: 70_000, 2: 150_000 } },
  kirchturm:         { GROW_ROOM: { 1: 90_000, 2: 160_000 },                    LABOR: {} },
  tiefgaragenabteil: { GROW_ROOM: {},                                           LABOR: {} },
  schrebergarten:    { GROW_ROOM: { 1: 100_000, 2: 170_000, 3: 280_000 },       LABOR: {} },
  doenerbude:        { GROW_ROOM: {},                                           LABOR: { 1: 180_000, 2: 320_000, 3: 600_000 } },
  kegelbahn:         { GROW_ROOM: { 1: 150_000, 2: 260_000, 3: 420_000 },       LABOR: { 1: 220_000 } },
  tankstelle:        { GROW_ROOM: {},                                           LABOR: { 1: 300_000, 2: 550_000 } },
  sparkassenfiliale: { GROW_ROOM: {},                                           LABOR: {} },
};
const EXPAND_HOURS: Record<string, Record<string, Record<number, number>>> = {
  dachboden:      { GROW_ROOM: { 1: 2, 2: 2 },          LABOR: { 1: 4 } },
  hinterzimmer:   { GROW_ROOM: { 1: 2, 2: 3 },          LABOR: { 1: 4, 2: 4 } },
  wohnung:        { GROW_ROOM: { 1: 2, 2: 3, 3: 4 },    LABOR: { 1: 4, 2: 4 } },
  kellerstudio:   { GROW_ROOM: { 1: 2, 2: 3 },          LABOR: { 1: 4, 2: 4, 3: 6 } },
  gartenlaube:    { GROW_ROOM: { 1: 1, 2: 2, 3: 3 },    LABOR: { 1: 4 } },
  campingwagen:   { GROW_ROOM: { 1: 2, 2: 3 },          LABOR: { 1: 4, 2: 4 } },
  bungalow:       { GROW_ROOM: { 1: 2, 2: 3, 3: 4 },    LABOR: { 1: 4, 2: 4, 3: 6 } },
  luxus_apartment:{ GROW_ROOM: { 1: 2, 2: 3, 3: 4 },    LABOR: { 1: 4, 2: 4, 3: 6 } },
  lagercontainer: { GROW_ROOM: { 1: 2, 2: 3 },          LABOR: { 1: 4, 2: 4, 3: 6 } },
  bootshaus:      { GROW_ROOM: { 1: 2, 2: 3, 3: 4 },    LABOR: { 1: 4, 2: 4 } },
  bunker:         { GROW_ROOM: { 1: 3, 2: 4, 3: 5 },    LABOR: { 1: 4, 2: 5, 3: 6 } },
  // NT-HIDEOUT-PROFILE-01 — neu (Backend-Sekunden / 3600)
  waschkeller:       { GROW_ROOM: { 1: 1.5 },               LABOR: { 1: 4, 2: 4 } },
  kirchturm:         { GROW_ROOM: { 1: 2, 2: 3 },           LABOR: {} },
  tiefgaragenabteil: { GROW_ROOM: {},                       LABOR: {} },
  schrebergarten:    { GROW_ROOM: { 1: 1.5, 2: 2.5, 3: 3.5 }, LABOR: {} },
  doenerbude:        { GROW_ROOM: {},                       LABOR: { 1: 4, 2: 4, 3: 6 } },
  kegelbahn:         { GROW_ROOM: { 1: 2, 2: 3, 3: 4 },     LABOR: { 1: 4 } },
  tankstelle:        { GROW_ROOM: {},                       LABOR: { 1: 4, 2: 4 } },
  sparkassenfiliale: { GROW_ROOM: {},                       LABOR: {} },
};

// Equipment-Optionen je Feld — feste, bekannte Schlüsselmenge (kein Record<string,...> mit
// dynamischem Lookup), daher als benanntes Interface statt Index-Signatur: dot-access auf
// diesen 11 Feldern bleibt dadurch garantiert nicht-undefined, kein `!` an den Call-Sites nötig.
interface GrowOptionsMap {
  tentItem:       Record<string, string>;
  lampItem:       Record<string, string>;
  potItem:        Record<string, string>;
  filterItem:     Record<string, string>;
  lampItem2:      Record<string, string>;
  potItem2:       Record<string, string>;
  lampItem3:      Record<string, string>;
  potItem3:       Record<string, string>;
  lampItem4:      Record<string, string>;
  potItem4:       Record<string, string>;
  irrigationItem: Record<string, string>;
}

const GROW_OPTIONS: GrowOptionsMap = {
  tentItem:       { '': '—', growzelt_klein: 'GROWZELT KLEIN (1 Topf)', growzelt_gross: 'GROWZELT GROSS (2 Töpfe)', growzelt_xl: 'GROWZELT PROFI (4 Töpfe)' },
  lampItem:       { '': '—', led_grow_lampe: 'LED GROW-LAMPE (−5% Zeit)', blacklight_x2: 'BLACKLIGHT X2 (−10% Zeit, +30% Qualität)', profi_lampe: 'PROFI-LAMPE (−15% Zeit, +40% Qualität)' },
  potItem:        { '': '—', anzuchttopf: 'ANZUCHTTOPF', hydroponik_topf: 'HYDROPONIK-TOPF (−15% Zeit)', naehrstoff_topf: 'NÄHRSTOFF-TOPF (+20% Ertrag)' },
  filterItem:     { '': '—', aktivkohlefilter: 'AKTIVKOHLEFILTER' },
  lampItem2:      { '': '—', led_grow_lampe: 'LED GROW-LAMPE (−5% Zeit)', blacklight_x2: 'BLACKLIGHT X2 (−10% Zeit, +30% Qualität)', profi_lampe: 'PROFI-LAMPE (−15% Zeit, +40% Qualität)' },
  potItem2:       { '': '—', anzuchttopf: 'ANZUCHTTOPF', hydroponik_topf: 'HYDROPONIK-TOPF (−15% Zeit)', naehrstoff_topf: 'NÄHRSTOFF-TOPF (+20% Ertrag)' },
  lampItem3:      { '': '—', led_grow_lampe: 'LED GROW-LAMPE (−5% Zeit)', blacklight_x2: 'BLACKLIGHT X2 (−10% Zeit, +30% Qualität)', profi_lampe: 'PROFI-LAMPE (−15% Zeit, +40% Qualität)' },
  potItem3:       { '': '—', anzuchttopf: 'ANZUCHTTOPF', hydroponik_topf: 'HYDROPONIK-TOPF (−15% Zeit)', naehrstoff_topf: 'NÄHRSTOFF-TOPF (+20% Ertrag)' },
  lampItem4:      { '': '—', led_grow_lampe: 'LED GROW-LAMPE (−5% Zeit)', blacklight_x2: 'BLACKLIGHT X2 (−10% Zeit, +30% Qualität)', profi_lampe: 'PROFI-LAMPE (−15% Zeit, +40% Qualität)' },
  potItem4:       { '': '—', anzuchttopf: 'ANZUCHTTOPF', hydroponik_topf: 'HYDROPONIK-TOPF (−15% Zeit)', naehrstoff_topf: 'NÄHRSTOFF-TOPF (+20% Ertrag)' },
  irrigationItem: { '': '—', bew_automatisch: 'AUTO-BEWÄSSERUNG (+10% Ertrag)' },
};

// ─── State ────────────────────────────────────────────────────────────────────

let hideouts:     Hideout[] = [];
let selectedIdx    = 0;
let loadError:    string | null = null;
let isLoading   = true;
let expandModal:  { expType: 'GROW_ROOM' | 'LABOR'; targetLevel: number } | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let reloadPromise: Promise<void> | null = null;
const pendingSlotEquip = new Map<string, Record<string, string | null>>();

// NT-HIDEOUT-PROFILE-01 — Achsen-Ausbau + Garagenplatz-Zuweisung
let axisUpgradeModal: { axis: AxisKey } | null = null;
let vehicleList: VehicleListItem[] = [];
const garageSlotSelection = new Map<number, string>(); // slotIndex → gewählte vehicleId

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtNox(cents: string | number): string {
  const n = typeof cents === 'string' ? parseInt(cents, 10) : cents;
  return (n / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₦';
}

function timeLeft(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'FERTIG';
  if (ms < 5 * 60_000) {
    const totalSec = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${String(h)}h ${String(m)}min` : `${String(m)}min`;
}

function suspColor(pct: number): string {
  if (pct >= 60) return 'var(--accent-red)';
  if (pct >= 30) return 'var(--accent-gold)';
  return 'var(--accent-green)';
}

function rerender(): void {
  const root = document.getElementById('immo-tab-root');
  if (!root) return;
  root.innerHTML = renderContent();
  attachTabListeners();
}

// ─── Render-Abschnitte ────────────────────────────────────────────────────────

function renderHideoutTabs(): string {
  if (hideouts.length === 0) return '';
  return `<div class="immo-htabs">
    ${hideouts.map((h, i) => `
      <button class="immo-htab${i === selectedIdx ? ' is-active' : ''}" data-immo-htab="${String(i)}">
        ${esc(formatHideoutName(h).toUpperCase())}
      </button>
    `).join('')}
  </div>`;
}

function renderOverview(h: Hideout): string {
  const susp      = Math.round(h.suspicionLevel);
  const suspC     = suspColor(susp);
  const isBuilding = h.expansionUpgradeCompletesAt
    ? new Date(h.expansionUpgradeCompletesAt).getTime() > Date.now()
    : false;

  let expStatus = 'KEIN AUSBAU';
  if (h.expansionType && h.expansionLevel > 0) {
    expStatus = `${h.expansionType === 'GROW_ROOM' ? 'GROW ROOM' : 'LABOR'} STUFE ${String(h.expansionLevel)}${isBuilding ? ' — UPGRADE LÄUFT' : ' — AKTIV'}`;
  }

  let progressBar = '';
  if (isBuilding && h.expansionUpgradeStartedAt && h.expansionUpgradeCompletesAt) {
    const total   = new Date(h.expansionUpgradeCompletesAt).getTime() - new Date(h.expansionUpgradeStartedAt).getTime();
    const elapsed = Date.now() - new Date(h.expansionUpgradeStartedAt).getTime();
    const pct     = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
    progressBar = `
      <div class="immo-upgrade-row">
        <span class="immo-upgrade-label">UPGRADE: STUFE ${String(h.expansionLevel + 1)} — noch ${timeLeft(h.expansionUpgradeCompletesAt)}</span>
        <div class="immo-prog-bar"><div class="immo-prog-fill" style="width:${String(pct)}%;"></div></div>
      </div>`;
  }

  return `
    <div class="immo-section">
      <p class="immo-section-title">ÜBERSICHT</p>
      <div class="immo-overview-grid">
        <span class="immo-key">NAME</span>
        <span class="immo-val">${esc(formatHideoutName(h).toUpperCase())}</span>
        <span class="immo-key">TYP</span>
        <span class="immo-val">${esc((HIDEOUT_TYPE_LABELS[h.subType] ?? h.subType).toUpperCase())}${h.isMobile ? ' <span style="color:var(--accent-purple);">MOBIL</span>' : ''}</span>
        <span class="immo-key">SPITZNAME</span>
        <span class="immo-val">
          <div class="immo-nickname-row">
            <input type="text" class="immo-nickname-input" data-immo-nickname-input="${esc(h.id)}" maxlength="30" placeholder="Kein Spitzname" value="${esc(h.nickname ?? '')}">
            <button class="immo-btn immo-btn--sm" data-immo-save-nickname="${esc(h.id)}">SPEICHERN</button>
          </div>
        </span>
        <span class="immo-key">MIETE</span>
        <span class="immo-val" style="color:var(--accent-gold);">${fmtNox(h.rentPerWeekCents)}/WOCHE</span>
        <span class="immo-key">VERDÄCHTIGKEIT</span>
        <span class="immo-val">
          <span style="color:${suspC};">${String(susp)}%</span>
          <span class="immo-susp-bar"><span class="immo-susp-fill" style="width:${String(Math.min(100, susp))}%;background:${suspC};"></span></span>
        </span>
        <span class="immo-key">AUSBAU</span>
        <span class="immo-val" style="color:${isBuilding ? 'var(--accent-orange)' : 'var(--accent-green)'};">${esc(expStatus)}</span>
      </div>
      ${progressBar}
    </div>`;
}

function renderExpansion(h: Hideout): string {
  const maxGrow  = MAX_GROW[h.subType]  ?? 0;
  const maxLabor = MAX_LABOR[h.subType] ?? 0;
  const isBuilding = h.expansionUpgradeCompletesAt
    ? new Date(h.expansionUpgradeCompletesAt).getTime() > Date.now()
    : false;

  function btnFor(expType: 'GROW_ROOM' | 'LABOR', maxLevel: number): string {
    const label  = expType === 'GROW_ROOM' ? 'GROW ROOM' : 'LABOR';
    const curLvl = h.expansionType === expType ? h.expansionLevel : 0;
    const nextLvl = curLvl + 1;
    const isActive = h.expansionType === expType && h.expansionLevel > 0;
    const isConflict = h.expansionType !== null && h.expansionType !== expType && h.expansionLevel > 0;
    const atMax  = curLvl >= maxLevel || maxLevel === 0;
    const cost   = EXPAND_CENTS[h.subType]?.[expType]?.[nextLvl];
    const hours  = EXPAND_HOURS[h.subType]?.[expType]?.[nextLvl];

    if (atMax) {
      return `<div class="immo-expand-info">
        <span class="immo-expand-name${isActive ? ' is-active' : ''}">${label}</span>
        <span class="immo-expand-max">MAX. STUFE ${String(curLvl)} ERREICHT</span>
      </div>`;
    }
    if (isConflict) {
      return `<div class="immo-expand-info">
        <span class="immo-expand-name">${label}</span>
        <button class="immo-btn" disabled title="Entweder/Oder — anderer Ausbau aktiv">AUSBAUEN S${String(nextLvl)}</button>
      </div>`;
    }
    if (isBuilding) {
      return `<div class="immo-expand-info">
        <span class="immo-expand-name${isActive ? ' is-active' : ''}">${label}</span>
        <button class="immo-btn" disabled>UMBAU LÄUFT</button>
      </div>`;
    }

    const costStr  = cost   ? fmtNox(cost)   : '—';
    const hoursStr = hours  ? `${String(hours)}h` : '—';

    return `<div class="immo-expand-info">
      <span class="immo-expand-name${isActive ? ' is-active' : ''}">${label} S${String(curLvl)} → S${String(nextLvl)}</span>
      <span class="immo-expand-cost">${costStr} · ${hoursStr} Umbauzeit</span>
      <button class="immo-btn immo-btn--expand"
        data-immo-expand="${expType}" data-immo-target="${String(nextLvl)}" data-immo-hid="${esc(h.id)}">
        AUSBAUEN
      </button>
    </div>`;
  }

  // Bestätigungs-Modal (inline)
  let modalHtml = '';
  if (expandModal !== null) {
    const { expType, targetLevel } = expandModal;
    const typeLabel = expType === 'GROW_ROOM' ? 'GROW ROOM' : 'LABOR';
    const cost      = EXPAND_CENTS[h.subType]?.[expType]?.[targetLevel];
    const hours     = EXPAND_HOURS[h.subType]?.[expType]?.[targetLevel];
    modalHtml = `
      <div class="immo-modal">
        <p class="immo-modal-title">AUSBAU BESTÄTIGEN</p>
        <p class="immo-modal-body">${esc(typeLabel)} Stufe ${String(targetLevel)}<br>
          Kosten: <b style="color:var(--accent-gold);">${cost ? fmtNox(cost) : '?'}</b> ·
          Dauer: <b>${hours ? String(hours) + 'h' : '?'}</b></p>
        <div class="immo-modal-actions">
          <button class="immo-btn immo-btn--confirm"
            data-immo-confirm-expand="${esc(expType)}" data-immo-hid="${esc(h.id)}">BESTÄTIGEN</button>
          <button class="immo-btn" data-immo-cancel-expand>ABBRECHEN</button>
        </div>
      </div>`;
  }

  return `
    <div class="immo-section">
      <p class="immo-section-title">AUSBAU</p>
      <div class="immo-expand-row">
        ${btnFor('GROW_ROOM', maxGrow)}
        ${btnFor('LABOR', maxLabor)}
      </div>
      ${modalHtml}
    </div>`;
}

// ─── Profil-Achsen LAGER/TARNUNG/GARAGE (NT-HIDEOUT-PROFILE-01) ───────────────

const AXIS_LABELS: Record<AxisKey, string> = {
  STORAGE: 'LAGER', CAMOUFLAGE: 'TARNUNG', GARAGE: 'GARAGE',
};

// Reine Anzeige-Duplikate — Backend: hideout-axis.constants.ts (kein Shared-Import für reine
// Anzeige-Werte, etabliertes Repo-Pattern). Nur für die TARNUNG-Prozentanzeige gebraucht.
const CAMOUFLAGE_RAID_REDUCTION_PER_LEVEL_DISPLAY = 0.15;
const CAMOUFLAGE_MAX_TOTAL_REDUCTION_DISPLAY = 0.6;

function axisStatusFor(h: Hideout, axis: AxisKey): HideoutAxisStatus {
  if (axis === 'STORAGE') return h.axisStatus.storage;
  if (axis === 'GARAGE') return h.axisStatus.garage;
  return h.axisStatus.camouflage;
}

function axisValueLabel(axis: AxisKey, status: HideoutAxisStatus): string {
  if (axis === 'STORAGE') return `${String(status.effectiveValue)} SLOTS`;
  if (axis === 'GARAGE') return `${String(status.effectiveValue)} PLÄTZE`;
  const reduction = Math.min(status.level * CAMOUFLAGE_RAID_REDUCTION_PER_LEVEL_DISPLAY, CAMOUFLAGE_MAX_TOTAL_REDUCTION_DISPLAY);
  return `STUFE ${String(status.level)} (−${String(Math.round(reduction * 100))}% RAZZIA)`;
}

function renderAxisCard(h: Hideout, axis: AxisKey): string {
  const status = axisStatusFor(h, axis);
  const label  = AXIS_LABELS[axis];

  if (status.isUpgrading && status.completesAt) {
    return `<div class="immo-expand-info">
      <span class="immo-expand-name is-active">${label}</span>
      <span class="immo-expand-cost">${esc(axisValueLabel(axis, status))} · Basis ${String(status.base)} → Max ${String(status.max)}</span>
      <button class="immo-btn" disabled>UMBAU LÄUFT — noch ${timeLeft(status.completesAt)}</button>
    </div>`;
  }

  if (status.nextCostCents === null || status.nextMinutes === null) {
    return `<div class="immo-expand-info">
      <span class="immo-expand-name">${label}</span>
      <span class="immo-expand-cost">${esc(axisValueLabel(axis, status))}</span>
      <span class="immo-expand-max">${status.max === 0 ? 'NICHT MÖGLICH' : 'MAX ERREICHT'}</span>
    </div>`;
  }

  return `<div class="immo-expand-info">
    <span class="immo-expand-name">${label}</span>
    <span class="immo-expand-cost">${esc(axisValueLabel(axis, status))} · Basis ${String(status.base)} → Max ${String(status.max)}</span>
    <span class="immo-expand-cost">${fmtNox(status.nextCostCents)} · ${String(status.nextMinutes)}min Umbauzeit</span>
    <button class="immo-btn immo-btn--expand"
      data-immo-axis-upgrade="${axis}" data-immo-hid="${esc(h.id)}">
      AUSBAUEN
    </button>
  </div>`;
}

function renderAxisUpgradeSection(h: Hideout): string {
  let modalHtml = '';
  if (axisUpgradeModal !== null) {
    const { axis } = axisUpgradeModal;
    const status = axisStatusFor(h, axis);
    modalHtml = `
      <div class="immo-modal">
        <p class="immo-modal-title">AUSBAU BESTÄTIGEN</p>
        <p class="immo-modal-body">${esc(AXIS_LABELS[axis])}<br>
          Kosten: <b style="color:var(--accent-gold);">${status.nextCostCents ? fmtNox(status.nextCostCents) : '?'}</b> ·
          Dauer: <b>${status.nextMinutes !== null ? `${String(status.nextMinutes)}min` : '?'}</b></p>
        <div class="immo-modal-actions">
          <button class="immo-btn immo-btn--confirm"
            data-immo-confirm-axis-upgrade="${esc(axis)}" data-immo-hid="${esc(h.id)}">BESTÄTIGEN</button>
          <button class="immo-btn" data-immo-cancel-axis-upgrade>ABBRECHEN</button>
        </div>
      </div>`;
  }

  return `
    <div class="immo-section">
      <p class="immo-section-title">PROFIL-AUSBAU</p>
      <div class="immo-expand-row immo-expand-row--3col">
        ${renderAxisCard(h, 'STORAGE')}
        ${renderAxisCard(h, 'CAMOUFLAGE')}
        ${renderAxisCard(h, 'GARAGE')}
      </div>
      ${modalHtml}
    </div>`;
}

// ─── Garagenplätze (NT-HIDEOUT-PROFILE-01) ────────────────────────────────────

/** Rolle eines Fahrzeugs auf einem Stellplatz — der Platz bleibt auch im Einsatz reserviert. */
const GARAGE_ROLE_LABELS: Record<string, string> = {
  DEALER: 'BEIM DEALER', JOB: 'JOB-FAHRZEUG', TRANSPORT: 'UNTERWEGS', AUFTRAG: 'UNTERWEGS',
  SMUGGLING: 'SCHMUGGEL', SEIZED: 'BESCHLAGNAHMT',
};

function renderGarageSlotsSection(h: Hideout): string {
  if (h.garageSlots.length === 0) return '';

  // NT-VIERTEL-FIX-01 (V1): Garagenplätze sind Heimat-Stellplätze — jedes garagenpflichtige
  // Fahrzeug, das nicht unterwegs ist, lässt sich hierher stellen (auch Dealer-/Job-Fahrzeuge).
  // Fahrzeuge ohne Stellplatz zuerst, Fahrzeuge dieses Verstecks ausgeblendet.
  const freeVehicles = vehicleList
    .filter((v) => v.requiresStellplatz && v.canMoveStellplatz && v.stellplatzPropertyId !== h.id)
    .sort((a, b) => Number(a.stellplatzPropertyId !== null) - Number(b.stellplatzPropertyId !== null));
  const ownerLabel = (v: VehicleListItem): string =>
    `${v.label}${v.stellplatzPropertyId === null ? ' (ohne Stellplatz)' : ' (umstellen)'}`;

  const rows = h.garageSlots.map((slot) => {
    if (slot.vehicleId) {
      const vehicle = vehicleList.find((v) => v.id === slot.vehicleId);
      const roleNote = vehicle && vehicle.role !== 'FREE' ? ` · ${esc(GARAGE_ROLE_LABELS[vehicle.role] ?? vehicle.role)}` : '';
      return `<div class="immo-garage-row">
        <span class="immo-garage-label">PLATZ ${String(slot.slotIndex + 1)}: ${esc(formatItemKey(slot.vehicleKey ?? ''))}${roleNote}</span>
        <button class="immo-btn immo-btn--sm" data-immo-garage-unassign="${String(slot.slotIndex)}" data-immo-hid="${esc(h.id)}">ABMELDEN</button>
      </div>`;
    }

    const selected = garageSlotSelection.get(slot.slotIndex) ?? '';
    const options = freeVehicles.length === 0
      ? '<option value="">– Kein Fahrzeug verfügbar –</option>'
      : [
          '<option value="">– wählen –</option>',
          ...freeVehicles.map((v) => `<option value="${esc(v.id)}"${selected === v.id ? ' selected' : ''}>${esc(ownerLabel(v))}</option>`),
        ].join('');

    return `<div class="immo-garage-row">
      <span class="immo-garage-label">PLATZ ${String(slot.slotIndex + 1)}: FREI</span>
      <select class="immo-select immo-garage-select" data-immo-garage-select="${String(slot.slotIndex)}">${options}</select>
      <button class="immo-btn immo-btn--sm" data-immo-garage-assign="${String(slot.slotIndex)}" data-immo-hid="${esc(h.id)}" ${selected ? '' : 'disabled'}>ZUWEISEN</button>
    </div>`;
  }).join('');

  return `
    <div class="immo-section">
      <p class="immo-section-title">GARAGENPLÄTZE</p>
      <div class="immo-garage-list">${rows}</div>
    </div>`;
}

function renderGrowSlot(slot: PropertySlot, expLevel: number, inventoryKeys: Set<string>): string {
  const pending       = pendingSlotEquip.get(slot.id) ?? {};
  const hasPending    = Object.keys(pending).length > 0;
  const tentEffective = ('tentItem' in pending) ? (pending.tentItem ?? null) : slot.tentItem;
  const hasTent       = Boolean(tentEffective);
  const isGross       = tentEffective === 'growzelt_gross' || tentEffective === 'growzelt_xl';
  const isXl          = tentEffective === 'growzelt_xl';

  function sel(field: keyof PropertySlot, opts: Record<string, string>, disabled: boolean): string {
    const cur       = (slot[field] as string | null) ?? '';
    const fieldStr  = field as string;
    const displayed = (fieldStr in pending) ? (pending[fieldStr] ?? '') : cur;
    const disAttr   = disabled ? 'disabled' : '';
    const optsHtml  = Object.entries(opts)
      .filter(([v]) => v === '' || inventoryKeys.has(v) || cur === v)
      .map(([v, lbl]) => `<option value="${esc(v)}"${displayed === v ? ' selected' : ''}>${esc(lbl)}</option>`)
      .join('');
    return `<select class="immo-select" data-immo-pending-slot="${esc(slot.id)}" data-immo-equip-field="${esc(field)}" ${disAttr}>${optsHtml}</select>`;
  }

  return `<div class="immo-slot-card">
    <p class="immo-slot-title">GROWZELT-SLOT ${String(slot.slotIndex + 1)}</p>
    <div class="immo-slot-grid">
      <span class="immo-key">ZELT</span>            ${sel('tentItem',       GROW_OPTIONS.tentItem,       false)}
      <span class="immo-key">LAMPE 1</span>         ${sel('lampItem',       GROW_OPTIONS.lampItem,       !hasTent)}
      <span class="immo-key">TOPF 1</span>          ${sel('potItem',        GROW_OPTIONS.potItem,        !hasTent)}
      <span class="immo-key">FILTER</span>          ${sel('filterItem',     GROW_OPTIONS.filterItem,     expLevel < 2)}
      ${isGross ? `
      <span class="immo-key immo-key--sub">LAMPE 2</span>         ${sel('lampItem2',     GROW_OPTIONS.lampItem2,     !hasTent)}
      <span class="immo-key immo-key--sub">TOPF 2</span>          ${sel('potItem2',      GROW_OPTIONS.potItem2,      !hasTent)}
      ` : ''}
      ${isXl ? `
      <span class="immo-key immo-key--sub">LAMPE 3</span>         ${sel('lampItem3',     GROW_OPTIONS.lampItem3,     !hasTent)}
      <span class="immo-key immo-key--sub">TOPF 3</span>          ${sel('potItem3',      GROW_OPTIONS.potItem3,      !hasTent)}
      <span class="immo-key immo-key--sub">LAMPE 4</span>         ${sel('lampItem4',     GROW_OPTIONS.lampItem4,     !hasTent)}
      <span class="immo-key immo-key--sub">TOPF 4</span>          ${sel('potItem4',      GROW_OPTIONS.potItem4,      !hasTent)}
      ` : ''}
      <span class="immo-key">BEWÄSSERUNG</span>     ${sel('irrigationItem', GROW_OPTIONS.irrigationItem, !hasTent)}
    </div>
    <div class="immo-slot-actions">
      <button class="immo-btn immo-btn--build" data-immo-build-slot="${esc(slot.id)}" ${!hasPending ? 'disabled' : ''}>AUFBAUEN</button>
      ${!hasTent && !hasPending ? '<span class="immo-inv-hint">Zelt aus Lager benötigt</span>' : ''}
    </div>
  </div>`;
}


function renderSlots(h: Hideout): string {
  if (h.expansionLevel === 0 || !h.expansionType) return '';

  // LABOR-Equipment wird im Crime Ops → LABOR Tab verwaltet
  if (h.expansionType === 'LABOR') {
    return `
      <div class="immo-section">
        <p class="immo-section-title">SLOT-VERWALTUNG</p>
        <p class="immo-hint">Labor-Equipment im Crime Ops → <strong style="color:var(--accent-gold);">LABOR</strong> Tab verwalten.</p>
      </div>`;
  }

  const unlocked = h.propertySlots.filter((s) => s.isUnlocked);
  if (unlocked.length === 0) return '';

  const inventoryKeys = new Set(h.inventory.map((i) => i.itemKey));
  const growSlots     = unlocked.filter((s) => s.slotType === 'GROW_TENT').sort((a, b) => a.slotIndex - b.slotIndex);
  const slotsHtml     = growSlots.map((s) => renderGrowSlot(s, h.expansionLevel, inventoryKeys)).join('');

  return `
    <div class="immo-section">
      <p class="immo-section-title">SLOT-VERWALTUNG</p>
      <p class="immo-hint">Equipment aus Lager auswählen, dann AUFBAUEN klicken.</p>
      <div class="immo-slots">${slotsHtml}</div>
    </div>`;
}

function renderInventory(h: Hideout): string {
  // Legacy-Items ausblenden; Items mit Menge 0 nie anzeigen
  const items = h.inventory.filter((item) =>
    item.itemType !== 'VEHICLE' && !item.itemKey.startsWith('kontakt_') && item.quantity > 0,
  );

  if (items.length === 0) {
    return `<div class="immo-section"><p class="immo-section-title">INVENTAR</p><p class="immo-empty">Leer.</p></div>`;
  }

  const rows = items.map((item) => {
    const expires = item.expiresAt
      ? `<span style="color:var(--accent-orange);">${timeLeft(item.expiresAt)}</span>`
      : '—';
    const displayName = formatItemKey(item.itemKey);
    const displayType = ITEM_TYPE_LABELS[item.itemType] ?? item.itemType.charAt(0).toUpperCase() + item.itemType.slice(1).toLowerCase();
    return `<tr>
      <td class="immo-td">${esc(displayName)}</td>
      <td class="immo-td">${String(item.quantity)}</td>
      <td class="immo-td">${esc(displayType)}</td>
      <td class="immo-td">${expires}</td>
    </tr>`;
  }).join('');

  return `
    <div class="immo-section">
      <p class="immo-section-title">INVENTAR (${String(h.currentLoad)} / ${String(h.slots)} SLOTS)</p>
      <table class="immo-table">
        <thead><tr>
          <th class="immo-th">ITEM</th><th class="immo-th">MENGE</th>
          <th class="immo-th">TYP</th><th class="immo-th">ABLAUF</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

// ─── Haupt-Render ─────────────────────────────────────────────────────────────

function renderContent(): string {
  if (isLoading) {
    return '<p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);padding:var(--space-4) 0;">LADE...</p>';
  }
  if (loadError) {
    return `<p style="font-family:var(--font-mono);font-size:12px;color:var(--accent-red);padding:var(--space-4) 0;">FEHLER: ${esc(loadError)}</p>`;
  }
  if (hideouts.length === 0) {
    return `<p class="immo-empty">Kein Versteck vorhanden. <a href="/immobilien/verstecke" style="color:var(--accent-gold);">Versteck kaufen</a>.</p>`;
  }

  const h = hideouts[selectedIdx];
  if (!h) return '';

  return `
    ${renderHideoutTabs()}
    ${renderOverview(h)}
    ${renderAxisUpgradeSection(h)}
    ${renderExpansion(h)}
    ${renderGarageSlotsSection(h)}
    ${renderSlots(h)}
    ${renderInventory(h)}
  `;
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

// NT-HOTFIX-01 (H5): Listener nur EINMAL pro Root-Element anhängen (Event-Delegation). Vorher
// hängte jeder rerender() neue click-/change-Listener an — ein Equipment-Wechsel feuerte dadurch
// nach einigen Re-Renders mehrfach parallel denselben Request.
let listenersRoot: HTMLElement | null = null;

function attachTabListeners(): void {
  const root = document.getElementById('immo-tab-root');
  if (!root || root === listenersRoot) return;
  listenersRoot = root;

  // Hideout-Tab wechseln
  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    const htab = target.closest<HTMLButtonElement>('[data-immo-htab]');
    if (htab) {
      const idx = parseInt(htab.dataset.immoHtab ?? '0', 10);
      if (idx !== selectedIdx) {
        selectedIdx = idx;
        expandModal = null;
        pendingSlotEquip.clear();
        axisUpgradeModal = null;
        garageSlotSelection.clear();
        rerender();
      }
      return;
    }

    // NT-HIDEOUT-PROFILE-01: Achsen-Ausbau-Button → Modal öffnen
    const axisUpgradeBtn = target.closest<HTMLButtonElement>('[data-immo-axis-upgrade]');
    if (axisUpgradeBtn && !axisUpgradeBtn.disabled) {
      axisUpgradeModal = { axis: axisUpgradeBtn.dataset.immoAxisUpgrade as AxisKey };
      rerender();
      return;
    }

    // Achsen-Ausbau-Modal bestätigen
    const confirmAxisBtn = target.closest<HTMLButtonElement>('[data-immo-confirm-axis-upgrade]');
    if (confirmAxisBtn && axisUpgradeModal) {
      const hid  = confirmAxisBtn.dataset.immoHid ?? '';
      const axis = confirmAxisBtn.dataset.immoConfirmAxisUpgrade as AxisKey;
      confirmAxisBtn.disabled = true;
      void handleAxisUpgrade(hid, axis);
      return;
    }

    // Achsen-Ausbau-Modal abbrechen
    if (target.closest('[data-immo-cancel-axis-upgrade]')) {
      axisUpgradeModal = null;
      rerender();
      return;
    }

    // Garagenplatz: Fahrzeug zuweisen
    const garageAssignBtn = target.closest<HTMLButtonElement>('[data-immo-garage-assign]');
    if (garageAssignBtn && !garageAssignBtn.disabled) {
      const hid       = garageAssignBtn.dataset.immoHid ?? '';
      const slotIndex = parseInt(garageAssignBtn.dataset.immoGarageAssign ?? '0', 10);
      garageAssignBtn.disabled = true;
      void handleGarageAssign(hid, slotIndex);
      return;
    }

    // Garagenplatz: Fahrzeug abmelden (F10-a: kein Cooldown, keine Kosten)
    const garageUnassignBtn = target.closest<HTMLButtonElement>('[data-immo-garage-unassign]');
    if (garageUnassignBtn && !garageUnassignBtn.disabled) {
      const hid       = garageUnassignBtn.dataset.immoHid ?? '';
      const slotIndex = parseInt(garageUnassignBtn.dataset.immoGarageUnassign ?? '0', 10);
      garageUnassignBtn.disabled = true;
      void handleGarageUnassign(hid, slotIndex);
      return;
    }

    // Ausbau-Button → Modal öffnen
    const expandBtn = target.closest<HTMLButtonElement>('[data-immo-expand]');
    if (expandBtn && !expandBtn.disabled) {
      const expType = expandBtn.dataset.immoExpand as 'GROW_ROOM' | 'LABOR';
      const target_ = parseInt(expandBtn.dataset.immoTarget ?? '1', 10);
      expandModal = { expType, targetLevel: target_ };
      rerender();
      return;
    }

    // Modal bestätigen
    const confirmBtn = target.closest<HTMLButtonElement>('[data-immo-confirm-expand]');
    if (confirmBtn && expandModal) {
      const hid     = confirmBtn.dataset.immoHid ?? '';
      const expType = confirmBtn.dataset.immoConfirmExpand as 'GROW_ROOM' | 'LABOR';
      confirmBtn.disabled = true;
      void handleExpand(hid, expType);
      return;
    }

    // Modal abbrechen
    if (target.closest('[data-immo-cancel-expand]')) {
      expandModal = null;
      rerender();
      return;
    }

    // Spitzname speichern
    const nickBtn = target.closest<HTMLButtonElement>('[data-immo-save-nickname]');
    if (nickBtn) {
      const hideoutId = nickBtn.dataset.immoSaveNickname ?? '';
      const input = root.querySelector<HTMLInputElement>(`[data-immo-nickname-input="${CSS.escape(hideoutId)}"]`);
      if (input) void handleSaveNickname(hideoutId, input.value);
      return;
    }

    // AUFBAUEN-Button (Grow-Equipment bestätigen)
    const buildBtn = target.closest<HTMLButtonElement>('[data-immo-build-slot]');
    if (buildBtn && !buildBtn.disabled) {
      const slotId  = buildBtn.dataset.immoBuildSlot ?? '';
      const pending = pendingSlotEquip.get(slotId);
      if (pending && Object.keys(pending).length > 0) {
        buildBtn.disabled    = true;
        buildBtn.textContent = '...';
        void handleBuildSlot(slotId, { ...pending });
      }
      return;
    }
  });

  // Equipment-Dropdown
  root.addEventListener('change', (e) => {
    const target = e.target as Element;

    // Labor-Equipment: sofort speichern
    const laborSel = target.closest<HTMLSelectElement>('[data-immo-equip-slot]');
    if (laborSel) {
      const slotId = laborSel.dataset.immoEquipSlot ?? '';
      const field  = laborSel.dataset.immoEquipField ?? '';
      const value  = laborSel.value || null;
      void handleEquip(slotId, field, value);
      return;
    }

    // Grow-Equipment: pending state aktualisieren (erst AUFBAUEN-Button bestätigt Speichern)
    const growSel = target.closest<HTMLSelectElement>('[data-immo-pending-slot]');
    if (growSel) {
      const slotId = growSel.dataset.immoPendingSlot ?? '';
      const field  = growSel.dataset.immoEquipField ?? '';
      const value  = growSel.value || null;
      let slotPending = pendingSlotEquip.get(slotId);
      if (!slotPending) {
        slotPending = {};
        pendingSlotEquip.set(slotId, slotPending);
      }
      slotPending[field] = value;
      rerender();
      return;
    }

    // NT-HIDEOUT-PROFILE-01: Garagenplatz-Fahrzeugauswahl
    const garageSel = target.closest<HTMLSelectElement>('[data-immo-garage-select]');
    if (garageSel) {
      const slotIndex = parseInt(garageSel.dataset.immoGarageSelect ?? '0', 10);
      if (garageSel.value) garageSlotSelection.set(slotIndex, garageSel.value);
      else garageSlotSelection.delete(slotIndex);
      rerender();
      return;
    }
  });
}

async function handleSaveNickname(hideoutId: string, value: string): Promise<void> {
  const trimmed = value.trim();
  try {
    const res = await api.patch<{ nickname: string | null }>(
      `/hideouts/${encodeURIComponent(hideoutId)}/nickname`,
      { nickname: trimmed === '' ? null : trimmed },
    );
    const h = hideouts.find((x) => x.id === hideoutId);
    if (h) h.nickname = res.nickname;
    showToast('Spitzname gespeichert.', 'info');
    rerender();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Spitzname konnte nicht gespeichert werden.', 'error');
  }
}

async function handleEquip(slotId: string, field: string, value: string | null): Promise<void> {
  // Slot im State suchen
  const hideout = hideouts[selectedIdx];
  if (!hideout) return;
  const slot = hideout.propertySlots.find((s) => s.id === slotId);
  if (!slot) return;

  // Alten Wert sichern für Rollback
  const oldValue = (slot[field as keyof PropertySlot] ?? null) as string | null;

  // Optimistisch anwenden
  (slot as unknown as Record<string, unknown>)[field] = value;

  try {
    await api.patch<unknown>(`/hideouts/slots/${encodeURIComponent(slotId)}/equipment`, {
      field,
      itemKey: value,
    });
    await reload();
  } catch (err) {
    // Rollback erfolgt über komplettes Neuladen des Hideouts
    (slot as unknown as Record<string, unknown>)[field] = oldValue;
    await reload();
    showToast(err instanceof ApiError ? err.message : 'Equipment-Update fehlgeschlagen.', 'error');
  }
}

async function handleBuildSlot(slotId: string, fields: Record<string, string | null>): Promise<void> {
  const hideout = hideouts[selectedIdx];
  const slot    = hideout?.propertySlots.find((s) => s.id === slotId);

  for (const [field, value] of Object.entries(fields)) {
    const oldValue = slot ? ((slot as unknown as Record<string, unknown>)[field] as string | null) : null;
    if (slot) (slot as unknown as Record<string, unknown>)[field] = value;
    try {
      await api.patch<unknown>(`/hideouts/slots/${encodeURIComponent(slotId)}/equipment`, {
        field,
        itemKey: value,
      });
    } catch (err) {
      if (slot) (slot as unknown as Record<string, unknown>)[field] = oldValue;
      await reload();
      showToast(err instanceof ApiError ? err.message : 'Equipment-Update fehlgeschlagen.', 'error');
      pendingSlotEquip.delete(slotId);
      return;
    }
  }

  pendingSlotEquip.delete(slotId);
  await reload();
  showToast('Equipment aufgebaut.', 'info');
}

async function handleExpand(hideoutId: string, expType: 'GROW_ROOM' | 'LABOR'): Promise<void> {
  expandModal = null;
  try {
    await api.post<unknown>(`/hideouts/${encodeURIComponent(hideoutId)}/expand`, { expansionType: expType });
    await reload();
    showToast('Umbau gestartet.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Umbau fehlgeschlagen.', 'error');
    rerender();
  }
}

// ─── NT-HIDEOUT-PROFILE-01: Achsen-Ausbau + Garagenplatz-Zuweisung ────────────

async function handleAxisUpgrade(hideoutId: string, axis: AxisKey): Promise<void> {
  axisUpgradeModal = null;
  try {
    await api.post<unknown>(`/hideouts/${encodeURIComponent(hideoutId)}/axis-upgrade`, { axis });
    await reload();
    showToast('Ausbau gestartet.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Ausbau fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleGarageAssign(hideoutId: string, slotIndex: number): Promise<void> {
  const vehicleId = garageSlotSelection.get(slotIndex);
  if (!vehicleId) { rerender(); return; }
  try {
    await api.post<unknown>(`/hideouts/${encodeURIComponent(hideoutId)}/garage/assign`, { slotIndex, vehicleId });
    garageSlotSelection.delete(slotIndex);
    await reload();
    showToast('Fahrzeug zugewiesen.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Zuweisung fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleGarageUnassign(hideoutId: string, slotIndex: number): Promise<void> {
  try {
    await api.post<unknown>(`/hideouts/${encodeURIComponent(hideoutId)}/garage/unassign`, { slotIndex });
    await reload();
    showToast('Fahrzeug abgemeldet.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abmelden fehlgeschlagen.', 'error');
    rerender();
  }
}

function showToast(msg: string, type: 'info' | 'error'): void {
  const color = type === 'error' ? 'var(--accent-red)' : 'var(--accent-gold)';
  const t = document.createElement('div');
  t.style.cssText = `position:fixed;bottom:80px;left:50%;transform:translateX(-50%);
    background:var(--bg-elevated);border:1px solid ${color};font-family:var(--font-mono);
    font-size:12px;color:${color};padding:8px 20px;z-index:500;border-radius:2px;
    letter-spacing:.05em;white-space:nowrap;`;
  t.textContent = msg;
  t.addEventListener('click', () => { t.remove(); });
  document.body.appendChild(t);
  setTimeout(() => { t.remove(); }, calcNoticeDisplayMs(msg, type === 'error'));
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  if (document.getElementById('immo-tab-styles')) return;
  const s = document.createElement('style');
  s.id = 'immo-tab-styles';
  s.textContent = `
    .immo-htabs{display:flex;gap:1px;flex-wrap:wrap;margin-bottom:var(--space-3);
      border-bottom:1px solid var(--border);padding-bottom:var(--space-2);}
    .immo-htab{font-family:var(--font-mono);font-size:11px;letter-spacing:.07em;
      text-transform:uppercase;padding:5px 12px;background:transparent;
      border:1px solid var(--border);border-radius:2px;cursor:pointer;
      color:var(--text-muted);}
    .immo-htab.is-active{border-color:var(--accent-gold);color:var(--accent-gold);}

    .immo-section{border:1px solid var(--border);border-radius:var(--radius);
      padding:var(--space-3);margin-bottom:var(--space-3);background:var(--bg-surface);}
    .immo-section-title{font-family:var(--font-mono);font-size:10px;letter-spacing:.1em;
      color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-2);}
    .immo-hint{font-family:var(--font-mono);font-size:11px;color:var(--text-italic);
      margin:0 0 var(--space-2);}
    .immo-empty{font-family:var(--font-mono);font-size:12px;color:var(--text-muted);
      padding:var(--space-2) 0;margin:0;}

    .immo-overview-grid{display:grid;grid-template-columns:120px 1fr;
      gap:4px var(--space-3);align-items:center;}
    .immo-key{font-family:var(--font-mono);font-size:10px;letter-spacing:.08em;
      color:var(--text-muted);text-transform:uppercase;}
    .immo-val{font-family:var(--font-mono);font-size:12px;color:var(--text-primary);
      display:flex;align-items:center;gap:var(--space-2);}
    .immo-susp-bar{flex:1;max-width:80px;height:5px;background:var(--bg-elevated);
      border:1px solid var(--border);border-radius:2px;overflow:hidden;display:inline-block;}
    .immo-susp-fill{height:100%;display:block;transition:width .3s;}

    .immo-upgrade-row{margin-top:var(--space-2);}
    .immo-upgrade-label{font-family:var(--font-mono);font-size:11px;
      color:var(--accent-orange);display:block;margin-bottom:4px;}
    .immo-prog-bar{height:6px;background:var(--bg-elevated);border:1px solid var(--border);
      border-radius:2px;overflow:hidden;}
    .immo-prog-fill{height:100%;background:var(--accent-orange);transition:width 1s;}

    .immo-expand-row{display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);}
    .immo-expand-info{display:flex;flex-direction:column;gap:4px;}
    .immo-expand-name{font-family:var(--font-mono);font-size:11px;letter-spacing:.07em;
      color:var(--text-muted);text-transform:uppercase;}
    .immo-expand-name.is-active{color:var(--accent-green);}
    .immo-expand-cost{font-family:var(--font-mono);font-size:11px;color:var(--text-italic);}
    .immo-expand-max{font-family:var(--font-mono);font-size:11px;color:var(--accent-green);
      letter-spacing:.05em;}

    .immo-expand-row--3col{grid-template-columns:1fr 1fr 1fr;}
    @media (max-width: 640px){.immo-expand-row--3col{grid-template-columns:1fr;}}

    .immo-garage-list{display:flex;flex-direction:column;gap:var(--space-2);margin-top:var(--space-2);}
    .immo-garage-row{display:flex;align-items:center;gap:var(--space-2);flex-wrap:wrap;
      padding:6px 8px;border:1px solid var(--border);border-radius:2px;background:var(--bg-elevated);}
    .immo-garage-label{font-family:var(--font-mono);font-size:var(--font-size-xs);
      letter-spacing:.05em;color:var(--text-primary);flex:1 1 auto;min-width:0;}
    .immo-garage-select{max-width:180px;}

    .immo-modal{border:1px solid var(--accent-orange);border-radius:var(--radius);
      padding:var(--space-3);margin-top:var(--space-3);background:var(--bg-elevated);}
    .immo-modal-title{font-family:var(--font-mono);font-size:11px;letter-spacing:.1em;
      color:var(--accent-orange);text-transform:uppercase;margin:0 0 var(--space-2);}
    .immo-modal-body{font-family:var(--font-mono);font-size:12px;color:var(--text-primary);
      line-height:1.6;margin:0 0 var(--space-2);}
    .immo-modal-actions{display:flex;gap:var(--space-2);}

    .immo-slots{display:flex;flex-direction:column;gap:var(--space-2);}
    .immo-slot-card{border:1px solid var(--border);border-radius:var(--radius);
      padding:var(--space-2) var(--space-3);background:var(--bg-elevated);}
    .immo-slot-title{font-family:var(--font-mono);font-size:10px;letter-spacing:.08em;
      color:var(--accent-gold);text-transform:uppercase;margin:0 0 var(--space-2);}
    .immo-slot-grid{display:grid;grid-template-columns:100px 1fr;gap:4px var(--space-2);
      align-items:center;}
    .immo-select{font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.04em;
      background:var(--bg-surface);border:1px solid var(--border);border-radius:2px;
      color:var(--text-primary);padding:4px 6px;width:100%;cursor:pointer;}
    .immo-select:disabled{opacity:.4;cursor:not-allowed;}
    .immo-nickname-row{display:flex;gap:var(--space-2);align-items:center;}
    .immo-nickname-input{font-family:var(--font-mono);font-size:var(--font-size-xs);
      background:var(--bg-surface);border:1px solid var(--border);border-radius:2px;
      color:var(--text-primary);padding:4px 6px;width:160px;}

    .immo-table{width:100%;border-collapse:collapse;font-family:var(--font-mono);font-size:0.95rem;}
    .immo-th{padding:4px 8px;text-align:left;font-size:0.875rem;letter-spacing:.08em;
      color:var(--text-muted);border-bottom:1px solid var(--border);text-transform:uppercase;}
    .immo-td{padding:5px 8px;border-bottom:1px solid color-mix(in srgb, var(--border) 50%, transparent);
      color:var(--text-primary);}

    .immo-btn{font-family:var(--font-mono);font-size:0.95rem;letter-spacing:.07em;
      text-transform:uppercase;padding:6px 14px;background:transparent;
      border-radius:var(--radius-btn);cursor:pointer;border:1px solid var(--border);
      color:var(--text-muted);display:inline-block;text-decoration:none;}
    .immo-btn:disabled{opacity:.38;cursor:not-allowed;}
    .immo-btn--expand {border-color:var(--accent-blue); color:var(--accent-blue);}
    .immo-btn--confirm{border-color:var(--accent-orange);color:var(--accent-orange);}
    .immo-btn--sm{padding:3px 8px;font-size:0.875rem;}
    .immo-key--sub{color:var(--accent-blue);opacity:.85;}
    .immo-slot-actions{margin-top:var(--space-2);display:flex;align-items:center;gap:var(--space-2);flex-wrap:wrap;}
    .immo-btn--build{border-color:var(--accent-green);color:var(--accent-green);}
    .immo-btn--build:disabled{border-color:var(--border);color:var(--text-muted);opacity:.38;}
    .immo-inv-hint{font-family:var(--font-mono);font-size:0.875rem;color:var(--text-muted);font-style:italic;}
  `;
  document.head.appendChild(s);
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

/** @param background true = Aufruf aus dem Polling-Timer (Render-Pause bei offener Auswahl, H7). */
async function reload(background = false): Promise<void> {
  if (reloadPromise) return reloadPromise;

  reloadPromise = (async () => {
    try {
      const [res, vehicleRes] = await Promise.all([
        api.get<{ hideouts: Hideout[] }>('/hideouts'),
        api.get<{ vehicles: VehicleListItem[] }>('/vehicle/list'),
      ]);
      hideouts    = res.hideouts;
      vehicleList = vehicleRes.vehicles;
      loadError   = null;

      // Pre-selected hideout aus sessionStorage
      const preId = sessionStorage.getItem('crimeops_hideout_id');
      if (preId) {
        sessionStorage.removeItem('crimeops_hideout_id');
        const idx = hideouts.findIndex((h) => h.id === preId);
        if (idx >= 0) selectedIdx = idx;
      }
      if (selectedIdx >= hideouts.length) selectedIdx = 0;
    } catch (err) {
      loadError = err instanceof ApiError ? err.message : 'Laden fehlgeschlagen.';
    }
    isLoading = false;
    if (background) renderWhenIdle(IMMO_RENDER_KEY, 'immo-tab-root', rerender);
    else rerender();
  })();

  try {
    await reloadPromise;
  } finally {
    reloadPromise = null;
  }
}

export async function refreshImmobilienTab(): Promise<void> {
  await reload();
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/** Initiales HTML-Skelett – wird von renderSectionContent() in crimeOps.ts eingebaut */
export function renderImmobilienTab(): string {
  return `<div id="immo-tab-root" style="padding:var(--space-2) 0;">
    <p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);padding:var(--space-4) 0;">LADE...</p>
  </div>`;
}

/** Aufgerufen wenn crimeOps die Sektion 'immobilien' aktiviert */
export function mountImmobilienTab(): void {
  addStyles();
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  isLoading       = hideouts.length === 0;
  loadError       = null;
  expandModal     = null;
  axisUpgradeModal = null;
  garageSlotSelection.clear();
  if (!isLoading) rerender();
  void reload();
  refreshTimer = setInterval(() => { void reload(true); }, 30_000);
}

/** Aufgerufen wenn crimeOps die Sektion verlässt */
export function cleanupImmobilienTab(): void {
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  listenersRoot = null;
  cancelPendingRender(IMMO_RENDER_KEY);
  hideouts    = [];
  loadError   = null;
  isLoading   = true;
  expandModal = null;
  selectedIdx = 0;
  pendingSlotEquip.clear();
  axisUpgradeModal = null;
  garageSlotSelection.clear();
  vehicleList = [];
}
